import {
  BRAND_BANNER_IMAGE_URL,
  BRAND_OG_IMAGE_URL,
  BRAND_PERSON_NAME,
  BRAND_SITE_URL,
} from "@/lib/brandConfig";
import { BaseTemplate } from "@/components/templates/BaseTemplate";
import { BaseTemplateInternational } from "@/components/templates/BaseTemplateInternational";
import { BioLinkTemplate } from "@/components/templates/BioLinkTemplate";
import { ArgentinaBioTemplate } from "@/components/templates/ArgentinaBioTemplate";
import { MaintenanceTemplate } from "@/components/templates/MaintenanceTemplate";
import BioTemplatePremium from "@/components/templates/BioTemplatePremium";
import BioTemplatePremiumSimple from "@/components/templates/BioTemplatePremiumSimple";
import {
  getCurrencyForCountry,
  getPlansForCurrency,
  isPremiumSpecialOfferActive,
} from "@/config/bioTemplatePremiumPlans";
import {
  getPremiumInvitePaths,
  PREMIUM_INVITE_FLOW_NAME,
} from "@/config/bioTemplatePremiumFlow";
import { isCloudinaryImage, isSameCloudinaryImage } from "@/lib/cloudinaryUtils";
import { brandDisplayNameFromSiteTitle } from "@/lib/siteBrandFromSettings";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import {
  normalizeCountryCode,
} from "@/lib/siteGeoPolicy";
import {
  isArExclusiveMuxHomeFromEdge,
  isMuxVideoPlayerHomeTemplate,
  resolveEffectiveHomeTemplate,
  shouldWaitForSiteSettingsBeforeHomeRender,
} from "@/lib/internationalHomeTemplate";
import { resolveMuxPlaybackOverrideForVisitor } from "@/lib/resolveInternationalHomeMuxPlayback";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { useSEO } from "@/hooks/useSEO";
import { usePortfolioSections } from "@/hooks/usePortfolioSections";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useSiteSettingsVariant } from "@/hooks/useSiteSettingsVariant";
import { invitacionExclusivaMuxSeo } from "@/lib/invitacionExclusivaMuxCopy";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import {
  WHATSAPP_BROADCAST_CONFIG,
  getWhatsAppBroadcastConfigForCountry,
} from "@/config/whatsappBroadcastConfig";
import { getGalleryPhotos } from "@/config/galleryConfig";
import { trackHomeCtaResolved } from "@/lib/homeCtaPosthogTracking";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";

// Code-split conversion templates — keeps initial JS smaller on 4G AR.
const DualCTAVideoLanding = lazy(() => import("@/pages/DualCTAVideoLanding"));
// InvitacionExclusivaMux is DualCTAVideoLanding with surface=invitacion_exclusiva
// (routes /invitacion-exclusive still use the thin wrapper page).

function MuxHomeFallback() {
  return <div className="fixed inset-0 bg-black" aria-hidden />;
}

// Dev-only admin chrome + debug helpers — never load on prod (AR home was importing province admin hooks).
const DevArgentinaProvinceAdmin = import.meta.env.DEV
  ? lazy(() =>
      import("@/components/dev/ArgentinaProvinceAdmin").then((m) => ({
        default: m.ArgentinaProvinceAdmin,
      })),
    )
  : null;

if (import.meta.env.DEV) {
  void import("@/lib/debugCta");
}

export const Index = () => {
  const templateSelection = useTemplateSelection();
  const { templateName, country, language, region, province, city } =
    templateSelection;
  const { setTemplateName } = useCurrentTemplate();

  const siteSettingsVariant = useSiteSettingsVariant();
  const { settings: siteSettings, isLoading: siteSettingsLoading } =
    useSiteSettings(siteSettingsVariant);

  // Edge inject: public AR geo + wishlist CTA flags (Exclusivo / dual_video).
  const edgeCfg = getServerTemplateConfig();
  // Prefer live edge flags (Worker inject + show_community cookie).
  const showCommunity =
    edgeCfg?.showCommunity === true ||
    (typeof document !== "undefined" &&
      /(?:^|;\s*)show_community=1(?:;|$)/.test(document.cookie));
  const showDualVideo = edgeCfg?.showDualVideo === true;

  /**
   * AR home:
   *  - país AR explícito, o
   *  - Worker inyectó plantilla AR (BaseTemplate*) sin ISO aún.
   * Público AR + Exclusivo (showCommunity) → DualCTAVideoLanding en init (abajo).
   */
  const isArgentinaVisitor = useMemo(() => {
    const code = normalizeCountryCode(country);
    if (code === "AR") return true;
    if (edgeCfg?.country === "AR") return true;
    if (code && code !== "UNKNOWN") return false;
    if (
      templateName === "DualCTAVideoLanding" ||
      templateName === "InvitacionExclusivaMux"
    ) {
      return false;
    }
    if (
      templateName === "BaseTemplate" ||
      templateName === "ArgentinaBioTemplate" ||
      templateName === "BaseTemplateBlack"
    ) {
      return true;
    }
    return false;
  }, [country, templateName, edgeCfg?.country]);

  const effectiveTemplateName = useMemo(
    () =>
      resolveEffectiveHomeTemplate({
        isArgentinaVisitor,
        country: isArgentinaVisitor ? "AR" : country,
        workerTemplateName: templateName,
        internationalHomeTemplate: siteSettings.internationalHomeTemplate,
        internationalHomeTemplateByCountry:
          siteSettings.internationalHomeTemplateByCountry,
        showCommunity,
        showDualVideo,
      }),
    [
      isArgentinaVisitor,
      templateName,
      country,
      showCommunity,
      showDualVideo,
      siteSettings.internationalHomeTemplate,
      siteSettings.internationalHomeTemplateByCountry,
    ],
  );

  /** AR Exclusivo (o dual_video) on `/` = same DualCTAVideoLanding as intl. */
  const arExclusiveMuxHome = isArExclusiveMuxHomeFromEdge({
    isArgentinaVisitor,
    showCommunity,
    showDualVideo,
  });

  // Skip portfolio network on Mux-first homes (DualCTA / Invitacion) — free 4G bandwidth.
  const needsPortfolioGallery = useMemo(() => {
    const t = effectiveTemplateName ?? "";
    if (!t) return isArgentinaVisitor && !arExclusiveMuxHome;
    if (isMuxVideoPlayerHomeTemplate(t)) return false;
    if (
      t === "MaintenanceTemplate" ||
      t === "BioTemplatePremium" ||
      t === "BioTemplatePremiumSimple"
    ) {
      return false;
    }
    return true;
  }, [effectiveTemplateName, isArgentinaVisitor, arExclusiveMuxHome]);

  // Portfolio: SWR cache; never blocks first paint (gallery hydrates in).
  const {
    allImageUrls: portfolioImageUrls,
    showPortfolioNavLink,
  } = usePortfolioSections(false, { enabled: needsPortfolioGallery });

  const waitForSiteSettings = shouldWaitForSiteSettingsBeforeHomeRender(
    isArgentinaVisitor,
    isMuxVideoPlayerHomeTemplate(effectiveTemplateName) || arExclusiveMuxHome,
  );

  // PostHog: AR pinta al toque; intl espera site-settings (o forceRender).
  useEffect(() => {
    if (siteSettingsLoading && waitForSiteSettings) return;
    setTemplateName(effectiveTemplateName ?? null);
    return () => setTemplateName(null);
  }, [effectiveTemplateName, siteSettingsLoading, waitForSiteSettings, setTemplateName]);

  // PostHog + funnel_events: full geo/CTA resolution (CABA none vs community, surface painted).
  useEffect(() => {
    if (siteSettingsLoading && waitForSiteSettings) return;
    if (!effectiveTemplateName) return;
    trackHomeCtaResolved({
      effectiveTemplate: effectiveTemplateName,
      arExclusiveMuxHome,
      isArgentinaVisitor,
      pathname:
        typeof window !== "undefined" ? window.location.pathname : "/",
    });
  }, [
    effectiveTemplateName,
    arExclusiveMuxHome,
    isArgentinaVisitor,
    siteSettingsLoading,
    waitForSiteSettings,
  ]);

  // Timeout corto: no dejar intl en pantalla negra. Alineado a SETTINGS_READY_MAX_MS (900).
  // AR no espera site-settings; portfolio ya no bloquea paint.
  const [forceRender, setForceRender] = useState(false);
  useEffect(() => {
    if (!waitForSiteSettings) {
      setForceRender(true);
      return;
    }
    const timer = setTimeout(() => {
      if (siteSettingsLoading) {
        if (import.meta.env.DEV) {
          console.warn("[Index] Loading timeout reached, forcing render with defaults");
        }
        setForceRender(true);
      }
    }, 900);
    return () => clearTimeout(timer);
  }, [siteSettingsLoading, waitForSiteSettings]);

  const brandName = useMemo(
    () => brandDisplayNameFromSiteTitle(siteSettings.title),
    [siteSettings.title],
  );

  const intlMuxSeo = useMemo(() => {
    if (
      effectiveTemplateName !== "InvitacionExclusivaMux" &&
      effectiveTemplateName !== "DualCTAVideoLanding"
    ) {
      return null;
    }
    const browserLang =
      typeof navigator !== "undefined" && navigator.language
        ? navigator.language
        : "en";
    return invitacionExclusivaMuxSeo(country, brandName || BRAND_PERSON_NAME, {
      workerLanguage: language,
      browserLang,
    });
  }, [effectiveTemplateName, country, brandName, language]);

  // Enhanced SEO configuration with region-specific optimization
  // When BaseTemplate is shown (LATAM/OTHER), use admin-configured title/description
  const isBaseTemplate = !["ArgentinaBioTemplate", "BioLinkTemplate", "MaintenanceTemplate", "BioTemplatePremium", "BioTemplatePremiumSimple", "DualCTAVideoLanding", "InvitacionExclusivaMux"].includes(effectiveTemplateName ?? "");
  const seoConfig = intlMuxSeo
    ? {
        title: intlMuxSeo.title,
        description: intlMuxSeo.description,
        keywords: `${brandName || BRAND_PERSON_NAME}, exclusive membership, premium video, Stripe checkout`,
        ogImage:
          BRAND_OG_IMAGE_URL,
        ogTitle: intlMuxSeo.ogTitle,
        ogDescription: intlMuxSeo.ogDescription,
        canonical: `${BRAND_SITE_URL}/`,
        author: BRAND_PERSON_NAME,
        publishedTime: "2026-01-01T00:00:00.000Z",
        modifiedTime: new Date().toISOString(),
        type: "website" as const,
        locale: intlMuxSeo.locale,
        alternateLocale: intlMuxSeo.alternateLocale,
        robots:
          "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" as const,
      }
    : {
    title: region === "US" 
      ? `${BRAND_PERSON_NAME} | Join My Community - Premium Content & Exclusive Access`
      : region === "SPAIN"
      ? `${BRAND_PERSON_NAME} | Únete a Mi Comunidad - Contenido Premium y Acceso Exclusivo`
      : isBaseTemplate
      ? siteSettings.title
      : `${BRAND_PERSON_NAME} | Modelo · Buenos Aires · Redes y crecimiento`,
    description: region === "US" 
      ? `Join ${BRAND_PERSON_NAME}'s exclusive community! Get access to premium Instagram content, join our Telegram group, and connect directly. Your gateway to exclusive lifestyle content and personal interaction.`
      : region === "SPAIN"
      ? `¡Únete a la comunidad exclusiva de ${BRAND_PERSON_NAME}! Accede a contenido premium de Instagram, únete a nuestro grupo de Telegram y conecta directamente. Tu puerta de entrada al contenido exclusivo de lifestyle e interacción personal.`
      : isBaseTemplate
      ? siteSettings.description
      : "Modelo de Buenos Aires, 18 años. Apasionada por las redes sociales y el crecimiento. Campañas, colaboraciones y comunidad.",
    keywords: region === "US"
      ? `${BRAND_PERSON_NAME}, premium content, exclusive access, Instagram content, Telegram group, bio link, content creator, lifestyle, community, exclusive content`
      : region === "SPAIN"
      ? `${BRAND_PERSON_NAME}, contenido premium, acceso exclusivo, contenido Instagram, grupo Telegram, bio link, creadora contenido, lifestyle, comunidad, contenido exclusivo, España`
      : `${BRAND_PERSON_NAME}, modelo Buenos Aires, 18 años, redes sociales, crecimiento, influencer, CABA`,
    ogImage: region === "US" 
      ? BRAND_OG_IMAGE_URL
      : region === "SPAIN"
      ? BRAND_OG_IMAGE_URL
      : BRAND_OG_IMAGE_URL,
    ogTitle: region === "US"
      ? `${BRAND_PERSON_NAME} | Join My Community - Exclusive Content Access`
      : region === "SPAIN"
      ? `${BRAND_PERSON_NAME} | Únete a Mi Comunidad - Acceso a Contenido Exclusivo`
      : isBaseTemplate
      ? siteSettings.title
      : `${BRAND_PERSON_NAME} | Modelo · Buenos Aires · Redes y crecimiento`,
    ogDescription: region === "US"
      ? "Join my exclusive community! Premium Instagram access, Telegram group, and direct connection. Let's create amazing memories together! ✨"
      : region === "SPAIN"
      ? "¡Únete a mi comunidad exclusiva! Acceso premium a Instagram, grupo de Telegram y conexión directa. ¡Vamos a crear recuerdos increíbles juntos! ✨"
      : isBaseTemplate
      ? siteSettings.description
      : "Modelo de Buenos Aires, 18 años. Redes sociales y crecimiento. Campañas, colaboraciones y comunidad.",
    canonical: `${BRAND_SITE_URL}/`,
    author: BRAND_PERSON_NAME,
    publishedTime: "2026-01-01T00:00:00.000Z",
    modifiedTime: new Date().toISOString(),
    type: region === "US" ? "profile" : region === "SPAIN" ? "profile" : "website",
    locale: region === "US" ? "en_US" : region === "SPAIN" ? "es_ES" : "es_AR",
    alternateLocale: region === "US" ? "es_AR" : region === "SPAIN" ? "en_US" : "en_US",
    // Homepage: index, follow for crawlers (BaseTemplate and other public templates)
    robots: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
  };

  useSEO(seoConfig);

  // Workaround: VITE_GALLERY_PHOTOS (comma-separated URLs) alimenta la galería si está definido
  const envGalleryPhotoItems = useMemo(
    () =>
      getGalleryPhotos().map((item, i) => ({
        id: i + 1,
        type: "photo" as const,
        src: item.src.trim(),
        title: "",
      })),
    []
  );
  const useEnvGalleryPhotos = envGalleryPhotoItems.length > 0;

  // Gallery: Cloudinary/portfolio/site settings; si hay VITE_GALLERY_PHOTOS, tiene prioridad
  const siteGalleryUrls = (siteSettings.galleryImageUrls ?? [])
    .filter((u): u is string => typeof u === "string" && u.trim().length > 0 && isCloudinaryImage(u))
    .map((u) => u.trim());
  const logoUrl = (siteSettings.logoUrl as string)?.trim() || "";
  const galleryWithoutLogo = logoUrl
    ? siteGalleryUrls.filter((u) => !isSameCloudinaryImage(logoUrl, u))
    : siteGalleryUrls;

  const portfolioUrls = portfolioImageUrls
    .filter((u) => u?.trim() && isCloudinaryImage(u))
    .map((u) => u.trim());
  const portfolioWithoutLogo = logoUrl
    ? portfolioUrls.filter((u) => !isSameCloudinaryImage(logoUrl, u))
    : portfolioUrls;

  // Galería: env → portfolio (si ya llegó) → site-settings gallery. No esperamos
  // portfolioLoading: en 4G AR se pinta shell + site gallery y se hidrata.
  const galleryMediaItems = useEnvGalleryPhotos
    ? envGalleryPhotoItems
    : portfolioWithoutLogo.length > 0
      ? (portfolioWithoutLogo.length > 1 ? portfolioWithoutLogo.slice(1) : portfolioWithoutLogo).map((src, i) => ({
          id: i + 1,
          type: "photo" as const,
          src: src.trim(),
          title: "",
        }))
      : galleryWithoutLogo.length > 0
        ? (galleryWithoutLogo.length > 1 ? galleryWithoutLogo.slice(1) : galleryWithoutLogo).map((src, i) => ({
            id: i + 1,
            type: "photo" as const,
            src: src.trim(),
            title: "",
          }))
        : [];

  const baseTemplateGalleryItems = useMemo(() => {
    if (useEnvGalleryPhotos) {
      const shuffled = [...envGalleryPhotoItems].sort(() => Math.random() - 0.5);
      return shuffled.map((item, i) => ({ ...item, id: i + 1 }));
    }
    // Prefer portfolio when ready; otherwise site-settings gallery so BaseTemplate
    // is not empty while /api/portfolio is in flight.
    const urls =
      portfolioWithoutLogo.length > 0 ? portfolioWithoutLogo : galleryWithoutLogo;
    if (urls.length === 0) return [];
    const shuffled = [...urls].sort(() => Math.random() - 0.5);
    return shuffled.map((src, i) => ({
      id: i + 1,
      type: "photo" as const,
      src: src.trim(),
      title: "",
    }));
  }, [
    useEnvGalleryPhotos,
    envGalleryPhotoItems,
    portfolioWithoutLogo,
    galleryWithoutLogo,
  ]);

  // Render template based on templateName from worker (useTemplateSelection reads __TEMPLATE_CONFIG__)
  const renderTemplate = () => {
    // MaintenanceTemplate always renders immediately — no network deps.
    if (effectiveTemplateName === "MaintenanceTemplate") {
      return <MaintenanceTemplate />;
    }

    // Only block home paint when we have *no* cached settings. With warm
    // cache, isLoading is false immediately (AR 4G / IG WebView).
    if (siteSettingsLoading && !forceRender && waitForSiteSettings) {
      return <div className="fixed inset-0 bg-black" aria-hidden />;
    }

    if (effectiveTemplateName === "ArgentinaBioTemplate") {
      return <ArgentinaBioTemplate mediaItems={galleryMediaItems} />;
    }

    if (effectiveTemplateName === "BioLinkTemplate") {
      return <BioLinkTemplate mediaItems={galleryMediaItems} />;
    }

    const countryMuxOverride = resolveMuxPlaybackOverrideForVisitor({
      country,
      province,
      city,
      muxByCountry: siteSettings.internationalHomeMuxByCountry,
      muxByArGeo: siteSettings.internationalHomeMuxByArGeo,
    });

    // Unified Mux VIP landing (DualCTAVideoLanding).
    // - Intl home: surface dual_cta
    // - AR Exclusivo (showCommunity) on `/`: same component, surface invitacion_exclusiva
    // - Route /invitacion-exclusive: same component via thin wrapper
    if (
      effectiveTemplateName === "DualCTAVideoLanding" ||
      effectiveTemplateName === "InvitacionExclusivaMux"
    ) {
      const exclusiveSurface =
        arExclusiveMuxHome ||
        effectiveTemplateName === "InvitacionExclusivaMux";
      return (
        <Suspense fallback={<MuxHomeFallback />}>
          <DualCTAVideoLanding
            country={isArgentinaVisitor ? "AR" : country}
            province={province}
            city={city}
            playbackIdOverride={countryMuxOverride}
            embeddedInHome
            surface={exclusiveSurface ? "invitacion_exclusiva" : "dual_cta"}
          />
        </Suspense>
      );
    }

    if (effectiveTemplateName === "BioTemplatePremium") {
      const currency = getCurrencyForCountry(country);
      const { standardPlan, vipPlan: baseVipPlan } = getPlansForCurrency(currency, language);
      const vipPlan =
        countryMuxOverride && baseVipPlan
          ? { ...baseVipPlan, playbackId: countryMuxOverride }
          : baseVipPlan;
      const { successPath, cancelPath } = getPremiumInvitePaths(currency);
      const whatsappBroadcast =
        getWhatsAppBroadcastConfigForCountry(country) ??
        (isPremiumSpecialOfferActive() ? WHATSAPP_BROADCAST_CONFIG : null);

      return (
        <BioTemplatePremium
          currency={currency}
          country={country}
          language={language}
          standardPlan={standardPlan}
          vipPlan={vipPlan}
        successPath={successPath}
        cancelPath={cancelPath}
        profileName={BRAND_PERSON_NAME}
        flowName={PREMIUM_INVITE_FLOW_NAME}
        whatsappBroadcast={whatsappBroadcast ?? undefined}
        />
      );
    }

    if (effectiveTemplateName === "BioTemplatePremiumSimple") {
      return (
        <BioTemplatePremiumSimple
          language={language}
          country={country}
          membershipPlaybackId={countryMuxOverride ?? undefined}
        />
      );
    }

    // BaseTemplate: never gate on portfolio (was "Cargando..." on AR 4G).
    // Gallery hydrates when /api/portfolio returns; site-settings/banner paint now.

    const bannerFromApiOrEnv =
      (typeof siteSettings.bannerImageUrl === "string" && siteSettings.bannerImageUrl.trim())
        ? siteSettings.bannerImageUrl.trim()
        : (BRAND_BANNER_IMAGE_URL?.trim() ?? "");
    const bannerImageUrl = bannerFromApiOrEnv
      ? bannerFromApiOrEnv
      : portfolioWithoutLogo.length > 0
        ? portfolioWithoutLogo[0]
        : useEnvGalleryPhotos
          ? envGalleryPhotoItems[0]?.src
          : galleryWithoutLogo[0];

    // Mismo criterio que i18n: src/lib/siteGeoPolicy.ts (AR, cookie AR, test AR).
    const workerCfg = getServerTemplateConfig();
    const useInternationalWishlist =
      !isArgentinaVisitor &&
      country.length === 2 &&
      country !== "UNKNOWN" &&
      workerCfg?.showWishlist === true;

    if (useInternationalWishlist) {
      return (
        <BaseTemplateInternational
          mediaItems={baseTemplateGalleryItems}
          skipMuxApiFilter={true}
          headerUrl={siteSettings.headerUrl}
          logoUrl={siteSettings.logoUrl}
          heroTitle={siteSettings.title}
          heroDescription={siteSettings.description}
          bannerImageUrl={bannerImageUrl}
          bannerDeferred={false}
          galleryDeferred={false}
          showPortfolioNavLink={showPortfolioNavLink}
          brandName={brandName}
          footerTagline={siteSettings.footerTagline}
          tallyContactFormId={siteSettings.tallyContactFormId}
          hideHeroCta={siteSettings.hideGlobalHeroCta}
        />
      );
    }

    return (
      <BaseTemplate
        mediaItems={baseTemplateGalleryItems}
        skipMuxApiFilter={true}
        headerUrl={siteSettings.headerUrl}
        logoUrl={siteSettings.logoUrl}
        heroTitle={siteSettings.title}
        heroDescription={siteSettings.description}
        bannerImageUrl={bannerImageUrl}
        bannerDeferred={false}
        galleryDeferred={false}
        showPortfolioNavLink={showPortfolioNavLink}
        brandName={brandName}
        footerTagline={siteSettings.footerTagline}
        tallyContactFormId={siteSettings.tallyContactFormId}
        hideHeroCta={siteSettings.hideGlobalHeroCta}
      />
    );
  };

  // Render template immediately - no loading screen
  return (
    <>
      {renderTemplate()}
      {DevArgentinaProvinceAdmin ? (
        <Suspense fallback={null}>
          <DevArgentinaProvinceAdmin />
        </Suspense>
      ) : null}
    </>
  );
};

export default Index;